<template lang="pug">
    .play-box
        template
            template(v-if='!isLoading')
                game-header(
                    :time='getTime'
                    :slug='type'
                    :game-type='parameters && parameters.courseType'
                    @soundStateChanged="headerKey = !headerKey",
                    :key="headerKey",
                    :is-homework="!!exerciseId"
                )
                repeat-after-me-container(
                    v-if="preparedItems && !gameFinished"
                    :prepared-items="preparedItems"
                    :different-figures="differentFigures"
                    :time="time"
                    :show-time="showTime"
                    :simultaneously="simultaneously"
                    :view-type="viewType"
                )
                .revived-numbers-game-container__result-container(v-if='gameFinished')
                    fireworks
                    .play-box__reaction-container
                        result-image.play-box__image(status='success')
                    result-buttons.result_buttons(
                        :exercise-id="exerciseId"
                        :game-name="type"
                        :games-type="parameters && parameters.courseType"
                        :show-restart-button="showRestartButton"
                        @restartGame="restartGame"
                    )
                        slot
            cs-loader(v-else)
</template>

<script>
    import playBoxGameMixin from '../../../../utils/trainer/playBoxGameMixin.js';
    import repeatAfterMeContainer from './RepeatAfterMeContainer.vue';
    import { mapState } from 'vuex';
    import _ from 'lodash';
    import trainerConfig from '../../../../config/trainer.json';

    const IMAGE_NAMES = {
        left: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27],
        right: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27],
    };

    function DEFAULT_DATA() {
        return {
            gameFinished: false,
            bgMusicInstance: null,
            isLoading: false,
            isMusicOn: false,
            differentFigures: false,
            showTime: 1,
            simultaneously: false,
            timeLimit: 0.5,
            viewType: '',
            items: [],
            loadedSound: 0,
        };
    }

    export default {
        mixins: [playBoxGameMixin],
        data() {
            return DEFAULT_DATA();
        },
        watch: {
            time(val) {
                if (val >= this.timeLimit * 60) {
                    this.finishGame();
                }
            },
        },
        mounted() {
            this.startGame();
        },
        methods: {
            assignDataFromParameters() {
                if (!this.gameParameters) {
                    return this.$router.push({ name: 'dashboard' });
                }
                this.isMusicOn = this.gameParameters.isMusicOn;
                this.showTime = this.gameParameters.showTime;
                this.simultaneously = this.gameParameters.simultaneously;
                this.timeLimit = this.gameParameters.timeLimit;
                this.viewType = 'figures';
                this.differentFigures = this.gameParameters.differentFigures;
            },
            startGame() {
                Object.assign(this, DEFAULT_DATA());
                this.assignDataFromParameters();

                this.$createjs.Sound.removeAllSounds();
                this.$createjs.Sound.removeAllEventListeners();
                this.$createjs.Sound.addEventListener('fileload', this.soundLoadTest);
                this.$createjs.Sound.alternateExtensions = ['mp3'];
                this.$createjs.Sound.registerSounds(this.repeatAfterMeSoundObjects);

                this.$nextTick(() => {
                    this.setTimer();
                });
            },
            soundLoadTest() {
                this.loadedSound++;
                if (this.loadedSound === this.repeatAfterMeSoundObjects.length && this.isMusicOn) {
                    this.bgMusicInstance = this.$createjs.Sound.play('music' + _.random(1, trainerConfig.music.length));
                    this.bgMusicInstance.on('complete', this.playRandomMusic, this);
                    this.bgMusicInstance.volume = 0.1;
                    this.isLoading = false;
                }
            },
            playRandomMusic() {
                this.bgMusicInstance.stop();
                this.bgMusicInstance = this.$createjs.Sound.play('music' + _.random(1, trainerConfig.music.length));
                this.bgMusicInstance.on('complete', this.playRandomMusic, this);
                this.bgMusicInstance.volume = 0.1;
            },
            finishGame() {
                if (this.isMusicOn) {
                    this.bgMusicInstance.stop();
                }
                this.stopTimer();
                this.soundInstance = this.$createjs.Sound.play('gameDone');
                this.gameFinished = true;
                this.sendStat();
            },
            restartGame() {
                Object.assign(this, DEFAULT_DATA());
                clearTimeout(this.counterTimeout);
                this.resetTimer();
                this.startGame();
            },
            refreshGameStatBeforeSend() {
                this.gameStat.parameters = _.cloneDeep(this.parameters);
                this.gameStat.answers = 0;
                this.gameStat.attempts = 1;
                this.gameStat.percent = 0;
                this.gameStat.isCompleted = true;
                this.gameStat.executionTime = this.time;
                this.gameStat.isCorrect = true;
            },
            fetchData() {
                this.getExercise();
            },
        },
        computed: {
            repeatAfterMeSoundObjects() {
                let sounds = [];
                sounds.push(...this.gamesConfig.common.sounds);
                sounds.push(...trainerConfig.music);
                return sounds;
            },
            ...mapState({
                gameParameters: state => state.trainer.game,
            }),
            preparedItems() {
                return IMAGE_NAMES;
            },
            showRestartButton() {
                return !this.exerciseId;
            },
        },
        components: {
            repeatAfterMeContainer,
        },
    };
</script>

<style scoped lang="scss">
    @import '@/assets/sass/games/playbox.scss';

    .play-box--warning {
        display: table;
    }

    .warning {
        display: table-cell;
        vertical-align: middle;
        text-align: center;
    }

    .play-box__reaction-container {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        width: 100%;
    }

    .play-box__time {
        font-weight: 500;
        font-size: 32px;
        line-height: 38px;
        text-align: center;
        color: $hsl-color-green-58;
        margin-bottom: 30px;
    }

    .play-box__reaction {
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        flex-grow: 1;
        flex-shrink: 1;
        flex-wrap: wrap;
        width: 100%;
        flex-direction: column;
        @include stack-space($space-base);
        @include media($md) {
            flex-wrap: nowrap;
        }
        @media screen and (max-width: 655px) {
            justify-content: space-between;
        }
    }

    .play-box__reaction-side {
        align-self: normal;
        font-weight: 500;
        font-size: 20px;
        margin-bottom: 10px;
        @include media($sm) {
            width: auto;
            font-size: 28px;
        }
        @include media($md) {
            display: flex;
            flex-direction: column;
            justify-content: center;
            height: 100%;
            flex-grow: 1;
            align-self: center;
            font-size: 20px;
        }
        @media screen and ($md) and (max-width: 800px) {
            font-size: 18px;
        }
        @include media($lg) {
            margin-bottom: 15px;
            font-size: 30px;
        }
        color: $hsl-color-green-58;
    }

    .result_buttons {
        margin-top: 10px;
        margin-bottom: 10px;
    }

    .play-box__reaction-side--correct {
        color: $hsl-color-green-58;
    }
</style>
